import { Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { NzProgressComponent } from 'ng-zorro-antd/progress';
import { AuthService } from 'core/services/auth.service';
import { animate, group, query, style, transition, trigger } from '@angular/animations';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ReferralModalFacade } from '../../../../../shared/services/referral-modal.facade';
import { ShareGanasModalComponent } from '../../../../../shared/modal/share-ganas-modal/share-ganas-modal.component';
import { HideTooltipOnScrollDirective } from 'shared/directives';
import {
  getUserLevelFromLeaves,
  LEVEL_LEAVES_REQUIREMENTS,
  MAX_USER_LEVEL,
  MIN_USER_LEVEL,
  UserLevel
} from 'shared/utils';

const LevelNames = {
  1: 'my-forest.muirWoods',
  2: 'my-forest.greatSmokyMountins',
  3: 'my-forest.cherokee',
  4: 'my-forest.shawnee',
  5: 'my-forest.ocala',
  6: 'my-forest.tongass'
};

@Component({
  selector: 'app-levels-widget',
  imports: [NgTemplateOutlet, TranslatePipe, NzProgressComponent, NzTooltipModule, HideTooltipOnScrollDirective],
  templateUrl: './levels-widget.component.html',
  styleUrl: './levels-widget.component.scss',
  animations: [
    trigger('slideAnimation', [
      transition(':increment', [
        group([
          query(':enter, :leave', style({ position: 'absolute', width: '100%' }), { optional: true }),
          query(':leave', [animate('300ms ease', style({ transform: 'translateX(-100%)' }))], { optional: true }),
          query(
            ':enter',
            [style({ transform: 'translateX(100%)' }), animate('300ms ease', style({ transform: 'translateX(0)' }))],
            { optional: true }
          )
        ])
      ]),
      transition(':decrement', [
        group([
          query(':enter, :leave', style({ position: 'absolute', width: '100%' }), { optional: true }),
          query(':leave', [animate('300ms ease', style({ transform: 'translateX(100%)' }))], { optional: true }),
          query(
            ':enter',
            [style({ transform: 'translateX(-100%)' }), animate('300ms ease', style({ transform: 'translateX(0)' }))],
            { optional: true }
          )
        ])
      ])
    ])
  ]
})
export class LevelsWidgetComponent {
  readonly authService = inject(AuthService);
  readonly referralModalFacade = inject(ReferralModalFacade);
  readonly #modalService = inject(NzModalService);

  readonly currentUserLeaves = computed(() => this.authService.currentUser$()?.leaves ?? 0);

  readonly currentUserLevel = computed(() => {
    const leaves = this.currentUserLeaves();
    return getUserLevelFromLeaves(leaves);
  });
  readonly currentSelectedLevel = linkedSignal<UserLevel>(this.currentUserLevel);

  readonly currentImage = computed(() => {
    const currentLevel = this.currentSelectedLevel();
    return `assets/img/my-forest/dk-l${currentLevel}.webp`;
  });
  readonly currentLevelName = computed(() => {
    const currentLevel = this.currentSelectedLevel();
    return LevelNames[currentLevel];
  });

  readonly leavesLabel = computed(() => {
    const currentLevel = this.needsLeavesToTheCurrentLevel();
    return currentLevel === 1 ? 'my-forest.oneLeave' : 'my-forest.leavesWord';
  });

  readonly yourForestNeeds = computed(() => {
    const currentLevel = this.currentSelectedLevel();
    const leavesRequired = LEVEL_LEAVES_REQUIREMENTS[currentLevel][0];
    return leavesRequired - this.currentUserLeaves();
  });

  readonly needsLeavesToTheCurrentLevel = computed(() => {
    const currentLevel = this.currentSelectedLevel();
    return LEVEL_LEAVES_REQUIREMENTS[currentLevel][0];
  });

  readonly needsLeavesToTheNextLevel = computed(() => {
    const currentLevel = this.currentSelectedLevel();
    return LEVEL_LEAVES_REQUIREMENTS[currentLevel][1];
  });

  readonly currentLevelProgress = computed(() => {
    const currentLevel = this.currentSelectedLevel();
    const leaves = this.currentUserLeaves();

    const minLeavesNum = LEVEL_LEAVES_REQUIREMENTS[currentLevel][0];
    const maxLeaves = LEVEL_LEAVES_REQUIREMENTS[currentLevel][1];

    if (maxLeaves === null) {
      return leaves >= minLeavesNum ? 100 : 0;
    }

    const range = maxLeaves - minLeavesNum;
    if (range <= 0) return 100;

    const progressInLevel = leaves - minLeavesNum;
    const progress = Math.round((progressInLevel / range) * 100);

    return Math.max(0, Math.min(100, progress));
  });

  readonly leavesNeededCount = computed(() => {
    const currentUserLevel = this.currentUserLevel();
    const currentSelectedLevel = this.currentSelectedLevel();

    if (currentUserLevel >= MAX_USER_LEVEL) {
      return 0;
    }

    if (currentSelectedLevel === currentUserLevel) {
      // Листья для следующего уровня
      const nextLevel = (currentUserLevel + 1) as UserLevel;
      if (nextLevel <= MAX_USER_LEVEL) {
        return LEVEL_LEAVES_REQUIREMENTS[nextLevel][0] - this.currentUserLeaves();
      }
    }

    if (currentSelectedLevel > currentUserLevel) {
      // Листья для достижения выбранного уровня
      const requiredLeaves = LEVEL_LEAVES_REQUIREMENTS[currentSelectedLevel][0];
      return requiredLeaves - this.currentUserLeaves();
    }

    return 0;
  });

  readonly levelMessageKey = computed(() => {
    const currentUserLevel = this.currentUserLevel();
    const currentSelectedLevel = this.currentSelectedLevel();

    if (currentUserLevel >= MAX_USER_LEVEL && currentSelectedLevel >= MAX_USER_LEVEL) {
      return 'my-forest.biggestForestMessage';
    }

    if (currentSelectedLevel === currentUserLevel) {
      return 'my-forest.reachNextLevelMessage';
    }

    if (currentSelectedLevel > currentUserLevel) {
      return 'my-forest.reachThisLevelMessage';
    }

    if (currentSelectedLevel < currentUserLevel) {
      return 'my-forest.outgrewForestMessage';
    }

    return '';
  });

  readonly levelMessageParams = computed(() => {
    const leavesCount = this.leavesNeededCount();
    return {
      count: leavesCount,
      leavesKey: leavesCount === 1 ? 'my-forest.oneLeave' : 'my-forest.leavesWord'
    };
  });

  nextLevelDisabled = computed(() => {
    return this.currentSelectedLevel() >= MAX_USER_LEVEL;
  });

  prevLevelDisabled = computed(() => {
    return this.currentSelectedLevel() <= MIN_USER_LEVEL;
  });

  onNextLevel(): void {
    const currentLevel = this.currentSelectedLevel();
    if (currentLevel < MAX_USER_LEVEL) {
      this.currentSelectedLevel.set((currentLevel + 1) as UserLevel);
    }
  }

  onPrevLevel(): void {
    const currentLevel = this.currentSelectedLevel();
    if (currentLevel > MIN_USER_LEVEL) {
      this.currentSelectedLevel.set((currentLevel - 1) as UserLevel);
    }
  }

  onAddNewReferral(): void {
    this.referralModalFacade.openAddReferralModal();
  }

  onShareGanas(): void {
    this.#modalService.create({
      nzContent: ShareGanasModalComponent,
      nzWrapClassName: 'modal-wrap',
      nzClosable: false,
      nzWidth: 555,
      nzClassName: 'modal-dialog'
    });
  }
}
